/**
 * The Mark-lodged block of the Mark-as-submitted dialog (R3 T6b, D3 slim
 * record): the files to keep (pre-ticked), when the bid was lodged, and an
 * optional reference number or receipt. Controlled — the dialog owns the
 * values and does the freezing on confirm.
 *
 * The receipt uploads as a bid document through the usual three steps
 * (presign, S3 PUT, register), the same path the outcome dialog's debrief
 * letter takes. It is frozen with the lodged files on confirm.
 */

import { useId, useState } from 'react'
import { Loader2, Lock, Paperclip } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { api, requestUploadUrl, toastApiError, uploadFileToS3 } from '@/lib/api'
import { validateFile } from '@/lib/schemas'
import { fileMetaLine } from '@/lib/submission-files-text'
import type { SubmissionFileItem } from '@/types/submission-files'

export interface LodgeReceipt {
  documentId: string
  name: string
}

interface LodgeFilesFieldsProps {
  bidId: string
  items: SubmissionFileItem[]
  ticked: ReadonlySet<string>
  onToggle: (itemId: string) => void
  /** `<input type="datetime-local">` value. */
  lodgedAt: string
  onLodgedAtChange: (value: string) => void
  reference: string
  onReferenceChange: (value: string) => void
  receipt: LodgeReceipt | null
  onReceiptChange: (receipt: LodgeReceipt | null) => void
  onUploadingChange: (uploading: boolean) => void
  disabled?: boolean
  /** The files are still loading: say so, rather than "no files". */
  isLoading?: boolean
}

export function LodgeFilesFields({
  bidId,
  items,
  ticked,
  onToggle,
  lodgedAt,
  onLodgedAtChange,
  reference,
  onReferenceChange,
  receipt,
  onReceiptChange,
  onUploadingChange,
  disabled = false,
  isLoading = false,
}: LodgeFilesFieldsProps) {
  const id = useId()
  const [isUploading, setIsUploading] = useState(false)
  const withFile = items.filter((item) => item.file)
  const withoutFile = items.length - withFile.length
  const tickedCount = withFile.filter((item) => ticked.has(item.itemId)).length

  const handleReceipt = async (file: File) => {
    const validation = validateFile(file)
    if (!validation.valid) {
      toast.error(validation.error ?? "That file can't be uploaded")
      return
    }
    setIsUploading(true)
    onUploadingChange(true)
    try {
      const { documentId, uploadUrl, s3Key } = await requestUploadUrl(file.name, file.type, file.size, bidId)
      await uploadFileToS3(uploadUrl, file)
      await api.post('/documents', {
        documentId,
        name: file.name,
        type: 'other',
        mimeType: file.type,
        size: file.size,
        s3Key,
        bidId,
      })
      onReceiptChange({ documentId, name: file.name })
    } catch (error) {
      toastApiError(error, "Couldn't upload the receipt")
    } finally {
      setIsUploading(false)
      onUploadingChange(false)
    }
  }

  return (
    <div className="space-y-3" aria-label="Files to keep as lodged" role="group">
      <div>
        <div className="flex items-center gap-1.5 text-sm">
          <Lock aria-hidden className="h-4 w-4 text-muted-foreground" />
          <span>
            {isLoading
              ? 'Loading the files to send…'
              : withFile.length === 0
              ? 'No files to keep yet'
              : `${tickedCount} ${tickedCount === 1 ? 'file' : 'files'} will be kept as lodged`}
          </span>
        </div>
        {withFile.length > 0 && (
          <div className="mt-2 space-y-1.5">
            {withFile.map((item) => (
              <label key={item.itemId} className="flex cursor-pointer items-start gap-2 text-sm">
                <Checkbox
                  checked={ticked.has(item.itemId)}
                  onCheckedChange={() => onToggle(item.itemId)}
                  aria-label={item.file!.name}
                  disabled={disabled}
                  className="mt-0.5"
                />
                <span className="min-w-0">
                  <span className="block truncate">{item.file!.name}</span>
                  <span className="block text-meta">
                    {item.title} · {fileMetaLine(item.file!)}
                  </span>
                </span>
              </label>
            ))}
          </div>
        )}
        {!isLoading && withoutFile > 0 && (
          <p className="mt-1.5 text-meta">
            {withoutFile} checklist {withoutFile === 1 ? 'item has' : 'items have'} no file yet.
          </p>
        )}
        <p className="mt-1.5 text-meta">
          BidContext keeps a copy of each ticked file exactly as it is now. Later edits don&apos;t change it.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <label htmlFor={`${id}-lodged-at`} className="text-meta">
            Lodged
          </label>
          <Input
            id={`${id}-lodged-at`}
            type="datetime-local"
            value={lodgedAt}
            onChange={(e) => onLodgedAtChange(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className="space-y-1">
          <label htmlFor={`${id}-reference`} className="text-meta">
            Reference number (optional)
          </label>
          <Input
            id={`${id}-reference`}
            value={reference}
            maxLength={500}
            placeholder="e.g. portal receipt number"
            onChange={(e) => onReferenceChange(e.target.value)}
            disabled={disabled}
          />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id={`${id}-receipt`}
          type="file"
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.txt"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = ''
            if (file) void handleReceipt(file)
          }}
        />
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          onClick={() => document.getElementById(`${id}-receipt`)?.click()}
        >
          {isUploading ? (
            <Loader2 aria-hidden className="mr-1 h-4 w-4 animate-spin" />
          ) : (
            <Paperclip aria-hidden className="mr-1 h-4 w-4" />
          )}
          {receipt ? 'Replace receipt' : 'Attach receipt (optional)'}
        </Button>
        {receipt && <span className="min-w-0 truncate text-meta">{receipt.name}</span>}
      </div>
    </div>
  )
}
